import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import * as S from './InvoicePromotionBanner.styles';
import { InvoicePromotionDetails } from './InvoicePromotionDetails';
import type { InvoicePromotion } from '@/features/Profile/sections/Packages/api';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { ModalDrawer } from '@/components/UI/ModalDrawer';
import { RightChevronIcon } from '@/components/UI/Icons/Icons';
import { useInvoicePromotionPackages } from '@/features/Profile/sections/Packages/hooks';

type InvoicePromotionBannerProps = {
  promotion: InvoicePromotion;
};

export function InvoicePromotionBanner({
  promotion,
}: InvoicePromotionBannerProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isDetailsOpen, , openDetails, closeDetails] =
    useToggleOpenState(false);
  const { isPackagesLoading, promotionPackages } =
    useInvoicePromotionPackages(isDetailsOpen);
  const detailsTitle = promotion.title ?? promotion.shortDescription ?? '';
  const modalTitle = (
    <S.ModalTitle
      $textColor={promotion.textColor}
      data-testid="invoice-promotion-title"
    >
      {promotion.iconUrl && <img alt="" src={promotion.iconUrl} />}
      <S.ModalTitleText>{detailsTitle}</S.ModalTitleText>
    </S.ModalTitle>
  );

  return (
    <>
      <S.Banner
        $bannerUrl={promotion.bannerUrl}
        $primaryColor={promotion.primaryColor}
        $secondaryColor={promotion.secondaryColor}
        $textColor={promotion.textColor}
        data-testid="invoice-promotion-banner"
      >
        {promotion.iconUrl && (
          <S.Icon alt="" src={promotion.iconUrl} role="presentation" />
        )}
        <S.Content>
          {promotion.title && <S.Title>{promotion.title}</S.Title>}
          {promotion.shortDescription && (
            <S.Description>{promotion.shortDescription}</S.Description>
          )}
        </S.Content>
        <S.DetailsButton type="button" onClick={openDetails}>
          <S.DetailsButtonText>{t('common.more_details')}</S.DetailsButtonText>
          <RightChevronIcon aria-hidden="true" />
        </S.DetailsButton>
      </S.Banner>

      <ModalDrawer
        background={theme.palette.primary.background}
        dataTestId="invoice-promotion-details"
        footerPaddingBottom={50}
        isOpen={isDetailsOpen}
        maxWidth={900}
        onClose={closeDetails}
        title={modalTitle}
      >
        <InvoicePromotionDetails
          isPackagesLoading={isPackagesLoading}
          onNavigate={closeDetails}
          packages={promotionPackages}
          promotion={promotion}
        />
      </ModalDrawer>
    </>
  );
}
